import { SelectOptionsHook } from "@coherehealth/common";

// Full 56-entry list matching the qm-service UnitedStates enum (includes territories).
// Used as static fallback when the server endpoint (/referenceData/states) is unavailable.
export const US_STATE_OPTIONS: string[] = [
  "AL",
  "AK",
  "AS",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "GU",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "MP",
  "OH",
  "OK",
  "OR",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VI",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

/**
 * Builds a `SelectOptionsHook<string>` over a static list, filtering by the typed query.
 * Mirrors the contract SingleSelect/MultipleSelect expect from a live rest-hook, so the
 * selects can be swapped to real data without touching the drawer components.
 */
export function makeStaticOptionsHook(items: string[]): SelectOptionsHook<string> {
  return ({ query }) => {
    const normalized = (query ?? "").trim().toLowerCase();
    const options = normalized ? items.filter((item) => item.toLowerCase().includes(normalized)) : items;
    return {
      options,
      optionsLoading: false,
      hasMoreOptions: false,
    };
  };
}

// Roles with access to every queue regardless of the Assigned queues list. Must match the
// backend's global-access role list. Compared in normalized form (ROLE_ prefix, underscores
// and case ignored), so "ROLE_SUPERVISOR" matches "SUPERVISOR".
export const GLOBAL_QUEUE_ACCESS_ROLES = ["SUPERVISOR", "ADMIN"];

export function hasGlobalQueueAccess(roles?: string[]): boolean {
  return (roles ?? []).some((role) =>
    GLOBAL_QUEUE_ACCESS_ROLES.includes(
      role
        .replace(/^ROLE_/i, "")
        .replace(/[^a-z0-9]/gi, "")
        .toUpperCase()
    )
  );
}

/** A queue option that displays by `name` but is stored/compared by its `id` (e.g. activeQueueId). */
export interface ActiveQueueOption {
  id: string;
  name?: string;
}

/**
 * Object variant of {@link makeStaticOptionsHook}: builds a `SelectOptionsHook<T>` over a static
 * list of objects, filtering by a `label`-derived string. Used for id-valued dropdowns (active
 * queue) where the display label and the stored value differ.
 */
export function makeStaticObjectOptionsHook<T>(items: T[], getLabel: (item: T) => string): SelectOptionsHook<T> {
  return ({ query }) => {
    const normalized = (query ?? "").trim().toLowerCase();
    const options = normalized ? items.filter((item) => getLabel(item).toLowerCase().includes(normalized)) : items;
    return {
      options,
      optionsLoading: false,
      hasMoreOptions: false,
    };
  };
}
